import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import type {
  DistributionLegendRow,
  LadderLegendRow,
  OptionsChartExpirationLevels,
  OptionsChartInvestorContext,
  OptionsChartOverlaySummary,
} from '@/utils/optionsPositioningChartOverlay';

interface OptionsPositioningChartLegendProps {
  summary: OptionsChartOverlaySummary;
  rows: DistributionLegendRow[];
  ladderRows: LadderLegendRow[];
  position: { left: number; top: number } | null;
  loading?: boolean;
  onClose?: () => void;
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatStrike(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function formatCompactOi(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

function biasBadgeClass(bias: string | null): string {
  switch (bias) {
    case 'bullish':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'bearish':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'neutral':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  }
}

function skewTone(char: string | null): 'bull' | 'bear' | 'neutral' {
  if (char === 'bearish_skew') return 'bear';
  if (char === 'bullish_skew') return 'bull';
  return 'neutral';
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{children}</p>
  );
}

function LevelChip({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'bull' | 'bear' | 'neutral' | 'accent';
}) {
  const toneClass =
    tone === 'bull'
      ? 'border-emerald-200 bg-emerald-50/90'
      : tone === 'bear'
        ? 'border-rose-200 bg-rose-50/90'
        : tone === 'accent'
          ? 'border-indigo-200 bg-indigo-50/90'
          : 'border-gray-200 bg-gray-50/90';

  return (
    <div className={`rounded-lg border px-2.5 py-2 ${toneClass}`}>
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-[13px] font-semibold tabular-nums text-gray-900">{value}</p>
      {sub ? <p className="text-[10px] leading-snug text-gray-500 mt-0.5">{sub}</p> : null}
    </div>
  );
}

function ExpirationLevelsPanel({
  levels,
  spot,
  investor,
}: {
  levels: OptionsChartExpirationLevels;
  spot: number;
  investor: OptionsChartInvestorContext;
}) {
  const hasLadder =
    levels.nearestAboveStrike != null || levels.nearestBelowStrike != null;

  return (
    <div className="space-y-2.5 border-b border-gray-100 px-3.5 py-3">
      {hasLadder ? (
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <SectionTitle>Implied close at expiry</SectionTitle>
            <span className="text-[15px] font-bold tabular-nums text-gray-900">
              ${formatStrike(spot)}
            </span>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {levels.nearestAboveStrike != null && levels.nearestAboveProbPct != null ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-2.5 py-2">
                <p className="text-[10px] font-medium text-emerald-700">Close above</p>
                <p className="text-[14px] font-semibold tabular-nums text-emerald-900">
                  ${formatStrike(levels.nearestAboveStrike)}
                </p>
                <p className="text-[12px] font-semibold tabular-nums text-emerald-700">
                  {formatPct(levels.nearestAboveProbPct)}
                </p>
              </div>
            ) : null}
            {levels.nearestBelowStrike != null && levels.nearestBelowProbPct != null ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50/80 px-2.5 py-2">
                <p className="text-[10px] font-medium text-rose-700">Close below</p>
                <p className="text-[14px] font-semibold tabular-nums text-rose-900">
                  ${formatStrike(levels.nearestBelowStrike)}
                </p>
                <p className="text-[12px] font-semibold tabular-nums text-rose-700">
                  {formatPct(levels.nearestBelowProbPct)}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {investor.peakRangeLabel != null && investor.peakRangeProbPct != null ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-2.5 py-2">
          <p className="text-[10px] font-medium text-indigo-600">Most likely close range</p>
          <p className="text-[13px] font-semibold text-indigo-900">{investor.peakRangeLabel}</p>
          <p className="text-[12px] font-semibold tabular-nums text-indigo-700">
            {formatPct(investor.peakRangeProbPct)}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        {levels.maxPainStrike != null ? (
          <LevelChip
            label="Max pain"
            value={`$${formatStrike(levels.maxPainStrike)}`}
            sub={
              levels.maxPainDistancePct != null
                ? `${levels.maxPainDistancePct > 0 ? '+' : ''}${levels.maxPainDistancePct.toFixed(1)}% vs spot`
                : undefined
            }
            tone="accent"
          />
        ) : null}
        {levels.callWallStrike != null ? (
          <LevelChip
            label="Call wall"
            value={`$${formatStrike(levels.callWallStrike)}`}
            sub="OI resistance"
            tone="bear"
          />
        ) : null}
        {levels.putWallStrike != null ? (
          <LevelChip
            label="Put wall"
            value={`$${formatStrike(levels.putWallStrike)}`}
            sub="OI support"
            tone="bull"
          />
        ) : null}
        {levels.gexFlipStrike != null ? (
          <LevelChip
            label="GEX flip"
            value={`$${formatStrike(levels.gexFlipStrike)}`}
            sub={
              levels.gexCharacterization
                ? `${levels.gexCharacterization} GEX · ${spot >= levels.gexFlipStrike ? 'above flip' : 'below flip'}`
                : undefined
            }
            tone="neutral"
          />
        ) : null}
      </div>
    </div>
  );
}

function PositioningContextPanel({
  levels,
  investor,
}: {
  levels: OptionsChartExpirationLevels;
  investor: OptionsChartInvestorContext;
}) {
  const skew = skewTone(investor.ivSkewCharacterization);
  const skewBorder =
    skew === 'bear'
      ? 'border-rose-200 bg-rose-50/60'
      : skew === 'bull'
        ? 'border-emerald-200 bg-emerald-50/60'
        : 'border-gray-200 bg-gray-50/60';

  return (
    <div className="space-y-2 border-b border-gray-100 px-3.5 py-3">
      <SectionTitle>Positioning & volatility</SectionTitle>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px] tabular-nums text-gray-700">
        {levels.putCallOiRatio != null ? (
          <div>
            <span className="text-gray-500">PCR (OI)</span>
            <p className="font-semibold text-gray-900">{levels.putCallOiRatio.toFixed(2)}</p>
          </div>
        ) : null}
        {levels.putCallVolumeRatio != null ? (
          <div>
            <span className="text-gray-500">PCR (vol)</span>
            <p className="font-semibold text-gray-900">{levels.putCallVolumeRatio.toFixed(2)}</p>
          </div>
        ) : null}
        {investor.callOi != null && investor.putOi != null ? (
          <div className="col-span-2">
            <span className="text-gray-500">Open interest</span>
            <p className="font-semibold text-gray-900">
              {formatCompactOi(investor.callOi)} calls · {formatCompactOi(investor.putOi)} puts
            </p>
          </div>
        ) : null}
        {investor.netDeltaOi != null ? (
          <div>
            <span className="text-gray-500">Net Δ OI</span>
            <p
              className={`font-semibold ${
                investor.netDeltaOi > 0 ? 'text-emerald-700' : investor.netDeltaOi < 0 ? 'text-rose-600' : 'text-gray-900'
              }`}
            >
              {investor.netDeltaOi > 0 ? '+' : ''}
              {formatCompactOi(investor.netDeltaOi)}
            </p>
          </div>
        ) : null}
        {investor.deltaOiBias ? (
          <div>
            <span className="text-gray-500">Δ bias</span>
            <p className="font-semibold capitalize text-gray-900">{investor.deltaOiBias}</p>
          </div>
        ) : null}
        {investor.atmCallIvPct != null && investor.atmPutIvPct != null ? (
          <div className="col-span-2">
            <span className="text-gray-500">ATM IV (call / put)</span>
            <p className="font-semibold text-gray-900">
              {investor.atmCallIvPct.toFixed(1)}% / {investor.atmPutIvPct.toFixed(1)}%
              {investor.ivAssessment ? (
                <span className="ml-1 font-normal text-gray-500">({investor.ivAssessment})</span>
              ) : null}
            </p>
          </div>
        ) : null}
      </div>
      {investor.ivSkewNote ? (
        <div className={`rounded-lg border px-2.5 py-2 ${skewBorder}`}>
          <p className="text-[10px] font-medium text-gray-600">IV skew</p>
          <p className="text-[11px] leading-snug text-gray-800">{investor.ivSkewNote}</p>
          {investor.otmPutIvPct != null && investor.otmCallIvPct != null ? (
            <p className="mt-0.5 text-[10px] tabular-nums text-gray-500">
              OTM put {investor.otmPutIvPct.toFixed(1)}% vs call {investor.otmCallIvPct.toFixed(1)}%
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ExpectedRangePanel({
  summary,
  levels,
}: {
  summary: OptionsChartOverlaySummary;
  levels: OptionsChartExpirationLevels;
}) {
  const moveLabel =
    levels.expectedMoveDollars != null && levels.expectedMovePct != null
      ? `±$${levels.expectedMoveDollars.toFixed(2)} (±${levels.expectedMovePct.toFixed(1)}%)`
      : null;

  return (
    <div className="border-b border-gray-100 px-3.5 py-3">
      <SectionTitle>Priced move into expiry</SectionTitle>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] tabular-nums text-gray-700">
        <span>
          Spot <span className="font-semibold text-gray-900">${summary.spot.toFixed(2)}</span>
        </span>
        {levels.atmStrike != null ? (
          <span>
            ATM <span className="font-semibold text-gray-900">${formatStrike(levels.atmStrike)}</span>
          </span>
        ) : null}
        {moveLabel ? (
          <span>
            Straddle <span className="font-semibold text-gray-900">{moveLabel}</span>
          </span>
        ) : null}
        <span>
          Priced range{' '}
          <span className="font-semibold text-gray-900">
            ${summary.lowerTarget.toFixed(2)}–${summary.upperTarget.toFixed(2)}
          </span>
        </span>
      </div>
      <p className="mt-1 text-[10px] leading-snug text-gray-500">
        Straddle-implied range (~1σ move priced by the market). Not a guarantee of where price
        closes.
      </p>
    </div>
  );
}

function DistributionTable({ rows }: { rows: DistributionLegendRow[] }) {
  const total = round1(rows.reduce((s, r) => s + r.probabilityPct, 0));

  return (
    <div className="px-2 pb-2">
      <div className="flex items-baseline justify-between px-1.5 pt-2 pb-1">
        <SectionTitle>Implied price distribution</SectionTitle>
        <span className="text-[10px] tabular-nums text-gray-400">Σ {total.toFixed(1)}%</span>
      </div>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] font-medium uppercase tracking-wide text-gray-400">
            <th className="px-2.5 py-1">Price range</th>
            <th className="px-2.5 py-1 text-right">Prob.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              className={
                row.isAtm
                  ? 'bg-indigo-50/90 text-indigo-950'
                  : row.isPeak
                    ? 'bg-amber-50/70 text-amber-950'
                    : row.isAggregate
                      ? 'text-gray-500'
                      : 'text-gray-800'
              }
            >
              <td className="px-2.5 py-1 font-medium leading-snug">
                {row.label}
                {row.isAtm ? (
                  <span className="ml-1 text-[9px] font-semibold uppercase text-indigo-500">ATM</span>
                ) : null}
                {row.isPeak && !row.isAtm ? (
                  <span className="ml-1 text-[9px] font-semibold uppercase text-amber-600">peak</span>
                ) : null}
              </td>
              <td className="px-2.5 py-1 text-right tabular-nums font-semibold">
                {formatPct(row.probabilityPct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-2.5 pt-1 text-[10px] leading-snug text-gray-500">
        Each row = chance price lands in that band at expiry. “Close above $X” uses cumulative delta
        at strike $X (not the sum of rows).
      </p>
    </div>
  );
}

const MARKET_READ_COLLAPSED_CHARS = 200;

function MarketReadPanel({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = text.length > MARKET_READ_COLLAPSED_CHARS;

  return (
    <div className="border-b border-gray-100 px-3.5 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <SectionTitle>Market read</SectionTitle>
        {canExpand ? (
          <button
            type="button"
            className="flex items-center gap-0.5 text-[10px] font-medium text-indigo-600 hover:text-indigo-800"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Show less' : 'Read all'}
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        ) : null}
      </div>
      <p
        className={`mt-1 text-[11px] leading-relaxed text-gray-700 ${
          !expanded && canExpand ? 'line-clamp-4' : ''
        }`}
      >
        {text}
      </p>
    </div>
  );
}

function LadderTable({ rows }: { rows: LadderLegendRow[] }) {
  if (!rows.length) return null;
  return (
    <table className="w-full text-[11px]">
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.label}
            className={row.direction === 'above' ? 'text-emerald-800' : 'text-rose-700'}
          >
            <td className="px-2.5 py-0.5 font-medium tabular-nums">{row.label}</td>
            <td className="px-2.5 py-0.5 text-right tabular-nums font-semibold">
              {formatPct(row.probabilityPct)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Distribution panel anchored at the historical price line endpoint.
 */
export function OptionsPositioningChartLegend({
  summary,
  rows,
  ladderRows,
  position,
  loading = false,
  onClose,
}: OptionsPositioningChartLegendProps) {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [ladderOpen, setLadderOpen] = useState(false);
  const dragSessionRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const { levels, investor } = summary;

  useEffect(() => {
    if (!position) setDragOffset({ x: 0, y: 0 });
  }, [position]);

  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
    setLadderOpen(false);
  }, [summary.expiration]);

  const endDrag = useCallback(() => {
    dragSessionRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onPointerMove = (e: PointerEvent) => {
      const session = dragSessionRef.current;
      if (!session || e.pointerId !== session.pointerId) return;
      setDragOffset({
        x: session.offsetX + (e.clientX - session.startClientX),
        y: session.offsetY + (e.clientY - session.startClientY),
      });
    };
    const onUp = (e: PointerEvent) => {
      const session = dragSessionRef.current;
      if (!session || e.pointerId !== session.pointerId) return;
      endDrag();
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, endDrag]);

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      dragSessionRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      };
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [dragOffset.x, dragOffset.y],
  );

  if (loading || !position) return null;

  const expLabel =
    summary.dte != null
      ? `Exp ${summary.expiration} · ${summary.dte}d to close`
      : `Exp ${summary.expiration}`;

  const bias = levels.directionalBias;
  const left = position.left + dragOffset.x;
  const top = position.top + dragOffset.y;

  return (
    <div
      className={`absolute z-[12] w-[26rem] max-w-[min(26rem,calc(100%-1rem))] max-h-[min(720px,78vh)] overflow-y-auto overflow-x-hidden rounded-xl border border-gray-200/90 bg-white/98 shadow-xl backdrop-blur-sm select-none ${
        isDragging ? 'cursor-grabbing' : ''
      }`}
      style={{ left, top }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={`relative sticky top-0 z-[1] border-b border-gray-100 bg-white/95 px-3.5 py-2.5 touch-none ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onPointerDown={onHeaderPointerDown}
        title="Drag to move"
      >
        <div className="flex items-start justify-between gap-2 pr-6">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-gray-900">Options at expiration</p>
            <p className="text-[11px] text-gray-500 tabular-nums">{expLabel}</p>
            {investor.analysisTimestamp ? (
              <p className="text-[10px] text-gray-400">
                As of {investor.analysisTimestamp.replace('T', ' ').slice(0, 16)} UTC
              </p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1">
            {bias ? (
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${biasBadgeClass(bias)}`}
              >
                {bias}
              </span>
            ) : null}
          </div>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close options panel"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        {levels.positioningNote ? (
          <p className="mt-1.5 text-[11px] leading-snug text-gray-600">{levels.positioningNote}</p>
        ) : null}
      </div>

      <ExpirationLevelsPanel levels={levels} spot={summary.spot} investor={investor} />
      <ExpectedRangePanel summary={summary} levels={levels} />
      <PositioningContextPanel levels={levels} investor={investor} />

      {investor.insightLine ? (
        <MarketReadPanel key={summary.expiration} text={investor.insightLine} />
      ) : null}

      {rows.length > 0 ? <DistributionTable rows={rows} /> : null}

      {ladderRows.length > 0 ? (
        <div className="border-t border-gray-100 px-2 pb-2">
          <button
            type="button"
            className="flex w-full items-center justify-between px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
            onClick={() => setLadderOpen((v) => !v)}
          >
            Strike ladder (near spot)
            {ladderOpen ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
          {ladderOpen ? <LadderTable rows={ladderRows} /> : null}
        </div>
      ) : null}

      <div className="sticky bottom-0 border-t border-gray-100 bg-gray-50/98 px-3.5 py-2 text-[10px] leading-relaxed text-gray-500">
        Options-implied probabilities from call/put deltas for this expiration close — not a
        price forecast. Combine with fundamentals, catalysts, and tape.
      </div>
    </div>
  );
}
